/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList
} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import {DatagridColumn} from './datagrid-column';
import {DatagridItems} from './datagrid-items';
import {DatagridPlaceholder} from './datagrid-placeholder';
import {DatagridRow} from './datagrid-row';
import {State} from './interfaces/state';
import {FiltersProvider} from './providers/filters';
import {ExpandableRowsCount} from './providers/global-expandable-rows';
import {HideableColumnService} from './providers/hideable-column.service';
import {Items} from './providers/items';
import {Page} from './providers/page';
import {RowActionService} from './providers/row-action-service';
import {Selection, SelectionType} from './providers/selection';
import {Sort} from './providers/sort';
import {StateDebouncer} from './providers/state-debouncer.provider';
import {StateProvider} from './providers/state.provider';
import {DatagridRenderOrganizer} from './render/render-organizer';
export class Datagrid implements AfterContentInit, AfterViewInit, OnDestroy {
/**
 * @param {?} columnService
 * @param {?} organizer
 * @param {?} items
 * @param {?} expandableRows
 * @param {?} selection
 * @param {?} rowActionService
 * @param {?} stateProvider
 */
constructor(private columnService: HideableColumnService,
private organizer: DatagridRenderOrganizer,
public items: Items,
public expandableRows: ExpandableRowsCount,
public selection: Selection,
public rowActionService: RowActionService,
private stateProvider: StateProvider) {}
public SELECTION_TYPE = SelectionType;
/**
 * Freezes the datagrid while data is loading
 * @return {?}
 */
public get loading(): boolean {
        return this.items.loading;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set loading(value: boolean) {
        this.items.loading = value;
    }
/**
 * Output emitted whenever the data needs to be refreshed, based on user action or external ones
 */
public refresh = new EventEmitter<State>(false);
/**
 * Public method to re-trigger the computation of displayed items manually
 * @return {?}
 */
public dataChanged() {
        this.items.refresh();
    }
/**
 * We grab the smart iterator from projected content
 */
public iterator: DatagridItems;
/**
 * Array of all selected items
 * @param {?} value
 * @return {?}
 */
set selected(value: any[]) {
        if (value) {
            this.selection.selectionType = SelectionType.Multi;
        } else {
            this.selection.selectionType = SelectionType.None;
        }
        this.selection.current = value;
    }

     selectedChanged = new EventEmitter<any[]>(false);
/**
 * Selected item in single-select mode
 * @param {?} value
 * @return {?}
 */
set singleSelected(value: any) {
        this.selection.selectionType = SelectionType.Single;
        if (value) {
            this.selection.currentSingle = value;
        } else {
            this.selection.currentSingle = null;
        }
    }

     singleSelectedChanged = new EventEmitter<any>(false);
/**
 * Selection/Deselection on row click mode
 * @param {?} value
 * @return {?}
 */
set rowSelectionMode(value: boolean) {
        this.selection.rowSelectionMode = value;
    }
/**
 * Indicates if all currently displayed items are selected
 * @return {?}
 */
public get allSelected() {
        return this.selection.isAllSelected();
    }
/**
 * Selects/deselects all currently displayed items
 * @param {?} value
 * @return {?}
 */
public set allSelected(value: boolean) {
        /*
         * This is a setter but we ignore the value.
         * It's strange, but it lets us have an indeterminate state where only
         * some of the items are selected.
         */
        this.selection.toggleAll();
    }
/**
 * Custom placeholder detection
 */
public placeholder: DatagridPlaceholder;
/**
 * Hideable Column data source / detection.
 */
public columns: QueryList<DatagridColumn>;
/**
 * When the datagrid is user-managed without the smart iterator, we get the items displayed
 * by querying the projected content. This is needed to keep track of the models currently
 * displayed, typically for selection.
 */
rows: QueryList<DatagridRow>;
/**
 * @return {?}
 */
ngAfterContentInit() {
        this._subscriptions.push(this.rows.changes.subscribe(() => {
            if (!this.items.smart) {
                this.items.all = this.rows.map((row: DatagridRow) => row.item);
            }
        }));
        if (!this.items.smart) {
            this.items.all = this.rows.map((row: DatagridRow) => row.item);
        }

        this._subscriptions.push(this.columns.changes.subscribe((columns: DatagridColumn[]) => {
            this.columnService.updateColumnList(this.columns.map(col => col.hideable));
        }));

        // Get ColumnService ready for HideableColumns.
        this.columnService.updateColumnList(this.columns.map(col => col.hideable));
    }
/**
 * Our setup happens in the view of some of our components, so we wait for it to be done before starting
 * @return {?}
 */
ngAfterViewInit() {
        // TODO: determine if we can get rid of provider wiring in view init so that subscriptions can be done earlier
        this.refresh.emit(this.stateProvider.state);
        this._subscriptions.push(this.stateProvider.change.subscribe(state => this.refresh.emit(state)));
        this._subscriptions.push(this.selection.change.subscribe(s => {
            if (this.selection.selectionType === SelectionType.Single) {
                this.singleSelectedChanged.emit(s);
            } else if (this.selection.selectionType === SelectionType.Multi) {
                this.selectedChanged.emit(s);
            }
        }));
    }
/**
 * Subscriptions to all the services and queries changes
 */
private _subscriptions: Subscription[] = [];
/**
 * @return {?}
 */
ngOnDestroy() {
        this._subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    }
/**
 * @return {?}
 */
resize(): void {
        this.organizer.resize();
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-datagrid",
    template: `
      <!--
        ~ Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
        ~ This software is released under MIT license.
        ~ The full license information can be found in LICENSE in the root directory of this project.
        -->

      <ng-content select="clr-dg-action-bar"></ng-content>
      <div class="datagrid-overlay-wrapper">
          <div class="datagrid-scroll-wrapper">
              <div class="datagrid" #datagrid>
                  <clr-dg-table-wrapper class="datagrid-table-wrapper">
                      <div clrDgHead class="datagrid-head">
                          <div class="datagrid-row datagrid-row-flex">
                              <!-- header for datagrid where you can select multiple rows -->
                              <div class="datagrid-column datagrid-select datagrid-fixed-column"
                                   *ngIf="selection.selectionType === SELECTION_TYPE.Multi">
                              <span class="datagrid-column-title">
                                  <clr-checkbox [(ngModel)]="allSelected"></clr-checkbox>
                              </span>
                                  <div class="datagrid-column-separator"></div>
                              </div>
                              <!-- header for datagrid where you can select one row only -->
                              <div class="datagrid-column datagrid-select datagrid-fixed-column"
                                   *ngIf="selection.selectionType === SELECTION_TYPE.Single">
                                  <div class="datagrid-column-separator"></div>
                              </div>
                              <!-- header for single row action; only display if we have at least one actionable row in datagrid -->
                              <div class="datagrid-column datagrid-row-actions datagrid-fixed-column"
                                   *ngIf="rowActionService.hasActionableRow">
                                  <div class="datagrid-column-separator"></div>
                              </div>
                              <!-- header for carets; only display if we have at least one expandable row in datagrid -->
                              <div class="datagrid-column datagrid-expandable-caret datagrid-fixed-column"
                                   *ngIf="expandableRows.hasExpandableRow">
                                  <div class="datagrid-column-separator"></div>
                              </div>
                              <ng-content select="clr-dg-column"></ng-content>
                          </div>
                      </div>

                      <ng-template *ngIf="iterator"
                                   ngFor [ngForOf]="items.displayed" [ngForTrackBy]="items.trackBy"
                                   [ngForTemplate]="iterator.template"></ng-template>
                      <ng-content *ngIf="!iterator"></ng-content>

                      <!-- Custom placeholder overrides the default empty one -->
                      <ng-content select="clr-dg-placeholder"></ng-content>
                      <clr-dg-placeholder *ngIf="!placeholder"></clr-dg-placeholder>
                  </clr-dg-table-wrapper>

                  <!--
                      This is not inside the table because there is no good way of having a single column span
                      everything when using custom elements with display:table-cell.
                  -->
                  <ng-content select="clr-dg-footer"></ng-content>
              </div>
          </div>
          <div class="datagrid-spinner" *ngIf="loading">
              <div class="spinner">Loading...</div>
          </div>
      </div>
    `,
    providers: [
        Selection, Sort, FiltersProvider, Page, Items, DatagridRenderOrganizer, RowActionService, ExpandableRowsCount,
        HideableColumnService, StateDebouncer, StateProvider
    ],
    host: {"[class.datagrid-host]": "true"}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: HideableColumnService, },
{type: DatagridRenderOrganizer, },
{type: Items, },
{type: ExpandableRowsCount, },
{type: Selection, },
{type: RowActionService, },
{type: StateProvider, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'loading': [{ type: Input, args: ["clrDgLoading", ] },],
'refresh': [{ type: Output, args: ["clrDgRefresh", ] },],
'iterator': [{ type: ContentChild, args: [DatagridItems, ] },],
'selected': [{ type: Input, args: ["clrDgSelected", ] },],
'selectedChanged': [{ type: Output, args: ["clrDgSelectedChange", ] },],
'singleSelected': [{ type: Input, args: ["clrDgSingleSelected", ] },],
'singleSelectedChanged': [{ type: Output, args: ["clrDgSingleSelectedChange", ] },],
'rowSelectionMode': [{ type: Input, args: ["clDgRowSelection", ] },],
'placeholder': [{ type: ContentChild, args: [DatagridPlaceholder, ] },],
'columns': [{ type: ContentChildren, args: [DatagridColumn, ] },],
'rows': [{ type: ContentChildren, args: [DatagridRow, ] },],
};
}

function Datagrid_tsickle_Closure_declarations() {
/** @type {?} */
Datagrid.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Datagrid.ctorParameters;
/** @type {?} */
Datagrid.propDecorators;
/** @type {?} */
Datagrid.prototype.SELECTION_TYPE;
/**
 * Output emitted whenever the data needs to be refreshed, based on user action or external ones
 * @type {?}
 */
Datagrid.prototype.refresh;
/**
 * We grab the smart iterator from projected content
 * @type {?}
 */
Datagrid.prototype.iterator;
/** @type {?} */
Datagrid.prototype.selectedChanged;
/** @type {?} */
Datagrid.prototype.singleSelectedChanged;
/**
 * Custom placeholder detection
 * @type {?}
 */
Datagrid.prototype.placeholder;
/**
 * Hideable Column data source / detection.
 * @type {?}
 */
Datagrid.prototype.columns;
/**
 * When the datagrid is user-managed without the smart iterator, we get the items displayed
 * by querying the projected content. This is needed to keep track of the models currently
 * displayed, typically for selection.
 * @type {?}
 */
Datagrid.prototype.rows;
/**
 * Subscriptions to all the services and queries changes
 * @type {?}
 */
Datagrid.prototype._subscriptions;
/** @type {?} */
Datagrid.prototype.columnService;
/** @type {?} */
Datagrid.prototype.organizer;
/** @type {?} */
Datagrid.prototype.items;
/** @type {?} */
Datagrid.prototype.expandableRows;
/** @type {?} */
Datagrid.prototype.selection;
/** @type {?} */
Datagrid.prototype.rowActionService;
/** @type {?} */
Datagrid.prototype.stateProvider;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
